import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  of,
  iif,
  throwError,
  merge,
  Subject,
  Observable,
} from 'rxjs';
import {
  map,
  switchMap,
  first,
  filter,
  distinctUntilChanged,
} from 'rxjs/operators';
import { User } from '@app/core/models';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { Actions, ofType } from '@ngrx/effects';
import { filterTruthy } from '@app/core/utils/rxjs.util';
import {
  getUser,
  getNotificationPreferences,
  getUserSuccess,
  getUserFailure,
} from '@app/store/user/user.actions';
import {
  selectUser,
  selectNotificationPreferences,
} from '@app/store/user/user.selectors';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _user = new BehaviorSubject<User.User | null>(null);
  private _notificationPreferences =
    new BehaviorSubject<User.NotificationPreferences | null>(null);
  private _newUser$ = new Subject<void>();

  constructor(private _store: Store<State>, private _actions$: Actions) {
    this._store.select(selectUser).subscribe((user) => this._user.next(user));

    this._store
      .select(selectNotificationPreferences)
      .subscribe((preferences) =>
        this._notificationPreferences.next(preferences),
      );

    this._user
      .pipe(
        map((user) => user && user.WebUserId),
        distinctUntilChanged(),
        filter((webUserId) => !!webUserId),
      )
      .subscribe(() => this._newUser$.next());
  }

  get newUser$() {
    return this._newUser$.asObservable();
  }

  get state() {
    return {
      user: this._user.value,
      notificationPreferences: this._notificationPreferences.value,
    };
  }

  get user(): User.User {
    return this._user.value!;
  }

  get user$(): Observable<User.User> {
    return this._user.pipe(filterTruthy());
  }

  getUser() {
    return this._user.pipe(filter((user) => !!user));
  }

  getNotificationPreferences() {
    return this._notificationPreferences.pipe(
      filter((preferences) => !!preferences),
    );
  }

  fetchUser() {
    this._store.dispatch(getUser());
    this._store.dispatch(getNotificationPreferences());

    return merge(
      this._actions$.pipe(
        ofType(getUserSuccess),
        map((action) => action.user),
      ),
      this._actions$.pipe(
        ofType(getUserFailure),
        map((action) => action.message),
      ),
    ).pipe(
      switchMap((value) =>
        iif(() => typeof value === 'object', of(value), throwError(value)),
      ),
      first(),
    );
  }

  isUser(user: {
    userId?: string;
    userID?: string;
    WebUserId?: string;
  }): boolean {
    if (!this.user) {
      return false;
    }
    const id = this.user.WebUserId;
    return user.userId === id || user.userID === id || user.WebUserId === id;
  }

  isNotUser(user: {
    userId?: string;
    userID?: string;
    WebUserId?: string;
  }): boolean {
    return !this.isUser(user);
  }
}
